import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import RenderMessage from '../ConverseClone/messages/RenderMessage';
import type { Message, ReplaySession, ReplayStep } from './types';

interface ConversationSharePlayerProps {
  sessionId: string;
  onDocumentUrlChange?: (url: string | null) => void;
  onTitleChange?: (title: string) => void;
}

function ConversationSharePlayer({
  sessionId,
  onDocumentUrlChange,
  onTitleChange,
}: ConversationSharePlayerProps): React.ReactNode {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replaySteps, setReplaySteps] = useState<ReplayStep[]>([]);
  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get document viewer from store
  const setStoreState = useAlchemystStoreForAi((store) => store.setStoreState);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [displayedMessages]);

  // Always close document viewer on component mount
  useEffect(() => {
    setStoreState({
      documentViewer: {
        isOpen: false,
        fileUrls: [],
      },
    });
  }, [setStoreState]);

  // Process files and documents from replay session
  const processSessionData = (data: ReplaySession) => {
    // Extract conversation title from first user message
    if (data.steps && data.steps.length > 0) {
      // Find the first user message
      const firstUserMessage = data.steps.find(step => step.role === 'user');
      if (firstUserMessage && firstUserMessage.message) {
        // Extract a title - either the first line or first 50 chars
        const message = firstUserMessage.message;
        const title = message.split('\n')[0].substring(0, 50);
        // If title is too short, use default
        if (title.length >= 10) {
          onTitleChange?.(title + (title.length >= 50 ? '...' : ''));
        }
      }
    }

    // Handle files
    if (data.files && data.files.length > 0) {
      const fileUrls = data.files.map((file) => file.url).filter(Boolean);
      if (fileUrls.length > 0) {
        setStoreState({
          documentViewer: {
            isOpen: true,
            fileUrls,
            activeTabIndex: 0,
          },
        });
        onDocumentUrlChange?.(fileUrls[0]);
      }
    }

    // Process steps for scratchpad content
    const scratchpadContent = data.steps
      .flatMap((step) => step.steps || [])
      .find((step) => step.type === 'todo_update')?.content;

    if (scratchpadContent) {
      const content =
        typeof scratchpadContent === 'string'
          ? scratchpadContent
          : JSON.stringify(scratchpadContent);
      setStoreState({
        documentViewer: {
          isOpen: true,
          fileUrls: [],
          scratchpadContent: content,
        },
      });
    }
  };

  // Check for file links in the steps array and open document viewer if found
  const checkForLinksAndOpenDocument = (message: Message) => {
    if (!message.steps) return false;

    const fileUrls: string[] = [];
    for (const step of message.steps) {
      if (step.type === 'data_update_file' && step.content && !step.error) {
        if (typeof step.content === 'string') {
          fileUrls.push(step.content);
        }
      }
    }

    if (fileUrls.length > 0) {
      setStoreState({
        documentViewer: {
          isOpen: true,
          fileUrls,
          activeTabIndex: 0,
        },
      });
      onDocumentUrlChange?.(fileUrls[0]);
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    const fetchRecording = async () => {
      try {
        setLoading(true);
        const response = await fetchWithRewrites(
          `/api/v1/replaysession/${sessionId}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch recording');
        }
        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Failed to load recording');
        }

        setReplaySteps(data.data.steps);
        processSessionData(data.data);
      } catch (err) {
        console.error('Error fetching recording:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load conversation'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRecording();
  }, [sessionId]);

  useEffect(() => {
    if (!isPlaying || currentStepIndex >= replaySteps.length) return;

    const currentStep = replaySteps[currentStepIndex];
    if (!currentStep.message) return;

    const message = currentStep.message;

    if (currentCharIndex === 0) {
      // Add new message when starting to type
      const newMessage: Message = {
        id: `${currentStepIndex}`,
        role: currentStep.role,
        message: '',
        json: typeof currentStep.json === 'string' ? currentStep.json : '',
        thinking_steps: currentStep.thinking_steps?.map((step) => ({
          ...step,
          error: step.error || null,
        })),
        steps: currentStep.steps?.map((step) => ({
          ...step,
          error: step.error || null,
          content: step.content || null,
        })),
        isThinking: false,
      };

      setDisplayedMessages((prev) => [...prev, newMessage]);
      checkForLinksAndOpenDocument(newMessage);
    }

    // Type character by character
    if (currentCharIndex < message.length) {
      const typingTimeout = setTimeout(() => {
        setDisplayedMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          newMessages[newMessages.length - 1] = {
            ...lastMessage,
            message: message.substring(0, currentCharIndex + 1),
          };
          return newMessages;
        });
        setCurrentCharIndex((prev) => prev + 1);
      }, 10);

      return () => {
        clearTimeout(typingTimeout);
      };
    }
    // Move to next message after a delay
    const nextMessageTimeout = setTimeout(() => {
      setCurrentStepIndex((prev) => prev + 1);
      setCurrentCharIndex(0);
    }, 300);

    return () => {
      clearTimeout(nextMessageTimeout);
    };
  }, [isPlaying, currentStepIndex, currentCharIndex, replaySteps]);

  const handlePlay = () => {
    setIsPlaying(true);
  };
  const handlePause = () => {
    setIsPlaying(false);
  };
  const handleRestart = () => {
    setDisplayedMessages([]);
    setCurrentStepIndex(0);
    setCurrentCharIndex(0);
    setIsPlaying(false);
    setStoreState({
      documentViewer: {
        isOpen: false,
        fileUrls: [],
      },
    });
    onDocumentUrlChange?.(null);
  };

  if (loading) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        minHeight="400px"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box alignItems="center" display="flex" gap={2}>
          {isPlaying ? (
            <Button
              onClick={handlePause}
              startIcon={<PauseIcon />}
              variant="contained"
            >
              Pause
            </Button>
          ) : (
            <Button
              disabled={currentStepIndex >= replaySteps.length}
              onClick={handlePlay}
              startIcon={<PlayArrowIcon />}
              variant="contained"
            >
              {currentStepIndex >= replaySteps.length ? 'Finished' : 'Play'}
            </Button>
          )}
          <Button
            onClick={handleRestart}
            startIcon={<RestartAltIcon />}
            variant="outlined"
          >
            Restart
          </Button>
          <Typography>
            {currentStepIndex}/{replaySteps.length} messages
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto', px: 2 }}>
        <Stack spacing={2}>
          {displayedMessages.map((message, index) => (
            <Box key={message.id || index} width="100%">
              <RenderMessage
                animate={false}
                isThinking={message.isThinking}
                json={message.json || ''}
                {...message}
                // message={message.message}
                id={message.id || crypto.randomUUID()}
                // role={message.role}
                // steps={message.steps}
                // thinking_steps={message.thinking_steps}
              />
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Stack>
      </Box>
    </Box>
  );
}

export default ConversationSharePlayer;
